import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { AnimatePresence, motion } from 'framer-motion'
import React, { ComponentProps, forwardRef, Ref, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { twMerge } from 'tailwind-merge'
import { Icon } from '../Icon/Icon'
import { getRatio } from './media.helpers'

export type ImageProps = {
  src: string
  alt: string
  aspectRatio?: '1:1' | '9:16' | '16:9' | '5:4' | '4:5'
  overlay?: boolean
  fit?: 'object-contain' | 'object-cover'
  blur?: boolean
  className?: string
  placeholderClassName?: string
  children?: React.ReactNode
} & ComponentProps<typeof motion.img>

/**
 * @param src name of image(with suffix)
 * @param alt image alt text
 * @param fit object-fit css property. contain or cover;
 */
export const Image = forwardRef(
  (
    {
      src,
      alt,
      children,
      aspectRatio,
      placeholderClassName,
      ...props
    }: ImageProps,
    imageRef: Ref<HTMLImageElement>,
  ) => {
    const [loaded, setLoaded] = useState(false)
    const [error, setError] = useState(false)

    const { ref, inView } = useInView({
      threshold: 0,
      delay: 300,
      triggerOnce: true,
    })

    // prettier-ignore
    const overlayClasses = props.overlay && loaded ? `bg-opacity-75 bg-blend-overlay bg-base-800` : ''
    const blurClasses = props.blur && loaded ? 'backdop-blur-sm' : ''

    return (
      <div
        ref={ref}
        // prettier-ignore
        className={`relative isolate overflow-hidden ${getRatio(aspectRatio)} ${props.className}`}>
        {/* Skeleton Loader */}
        <AnimatePresence>
          {!loaded && (
            <motion.div
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 1 }}
              className={twMerge(
                `absolute inset-0 flex justify-center items-center z-20 bg-base-200 animate-pulse`,
                placeholderClassName,
              )}>
              <Icon
                icon={faSpinner}
                size='lg'
                className='text-base-0/30 animate-spin'
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Actual Video */}
        <AnimatePresence>
          {!error && (
            <motion.img
              {...props}
              ref={imageRef}
              animate={{ opacity: inView && loaded ? 1 : 0 }}
              src={src}
              // In a video tag we use 'onLoadedData' because it triggers when the first part of the data has loaded
              // 'onLoad' is useful for images because it waits until the full data is loaded
              onLoad={() => setLoaded(true)}
              // the native error event given from onError tells us literally nothing, which is why we only have a boolean state
              onError={() => setError(true)}
              className={`absolute z-10 inset-0 h-full w-full ${
                props.fit || 'object-cover'
              }`}
            />
          )}
        </AnimatePresence>
        {/* <img src={src} {...props} /> */}
        {/* Theoretically, we should add src sets here */}

        {error && (
          <div
            className={`flex absolute z-10 overflow-hidden inset-0 object-cover items-center justify-center bg-base-200`}>
            <p className='font-medium text-xs text-center'>
              image failed to load
            </p>
          </div>
        )}

        {/* Children are placed on top of the photo */}
        {children && (
          <div
            className={`z-30 overflow-hidden absolute inset-0 ${overlayClasses} ${blurClasses}`}>
            {children}
          </div>
        )}
      </div>
    )
  },
)
